import { formatCurrency } from '@angular/common';
import { Injectable } from '@angular/core';
import { Product } from '@shared/models/product.model';
import { SeperatedColumn } from '@shared/models/seperated-columns';
import { Order, OrderException, OrderHeader, OrderItem } from '@usf/ngrx-order';
import { CurrencyService } from '../currency.service';
import { DateService } from '../date.service';
import { ProductPropertiesEnum } from '@usf/product-types';
import { Customer } from '@usf/customer-types';
import { OrderStatusDetails } from '@shared/constants/order-status-title.enum';

@Injectable({
  providedIn: 'root',
})
export class OrderDownloadDtoService {
  /**
   *
   * @param products Contains product info
   * @param submittedOrder Contains Order (Header, OrderItems)
   * @returns
   */
  static createSubmittedSeperatedColumns(
    products: Map<number, Product>,
    submittedOrder: Order,
  ): SeperatedColumn[] {
    const seperatedColumns: SeperatedColumn[] = [
      {
        columnName: 'CUSTOMER NUMBER',
        singleValue: `" ${submittedOrder.orderHeader.customerNumber}"`,
        totals: '',
      },
      {
        columnName: 'DISTRIBUTOR',
        singleValue: `" ${submittedOrder.orderHeader.divisionNumber || 0}"`,
        totals: '',
      },
      {
        columnName: 'DEPARTMENT',
        singleValue: `" ${submittedOrder.orderHeader.departmentNumber || 0}"`,
        totals: '',
      },
      {
        columnName: 'DATE',
        singleValue: `" ${DateService.formatDateMaintainUTC(
          submittedOrder.orderHeader.confirmedDeliveryDate,
        )}"`,
        totals: '',
      },
      {
        columnName: 'PO NUMBER',
        singleValue: `" ${submittedOrder.orderHeader.purchaseOrderNumber}"`,
        totals: 'Totals',
      },
      {
        columnName: 'PRODUCT NUMBER',
        columnValues: submittedOrder.orderItems.map(
          oi => `${oi.productNumber}`,
        ),
        totals: '',
      },
      {
        columnName: 'CUST PROD #',
        singleValue: '',
        totals: '',
      },
      {
        columnName: 'DESCRIPTION',
        columnValues: submittedOrder.orderItems.map(
          oi =>
            `" ${OrderDownloadDtoService.escapeSeperatedValues(
              products.get(oi.productNumber)?.summary?.productDescTxtl ||
                'PRODUCT NOT FOUND',
            )}"`,
        ),
        totals: '',
      },
      {
        columnName: 'BRAND',
        columnValues: submittedOrder.orderItems.map(
          oi =>
            `" ${OrderDownloadDtoService.escapeSeperatedValues(
              products.get(oi.productNumber)?.summary?.brand ||
                'PRODUCT NOT FOUND',
            )}"`,
        ),
        totals: '',
      },
      {
        columnName: 'PACK SIZE',
        columnValues: submittedOrder.orderItems.map(
          oi =>
            `" ${OrderDownloadDtoService.escapeSeperatedValues(
              products.get(oi.productNumber)?.summary?.salesPackSize ||
                'PRODUCT NOT FOUND',
            )}"`,
        ),
        totals: '',
      },
      {
        columnName: 'CS PRICE',
        columnValues: submittedOrder.orderItems.map(
          oi => `" ${CurrencyService.toUSDString(oi?.unitPrice.toFixed(2))}"`,
        ),
        totals: '',
      },
      {
        columnName: 'EA PRICE',
        columnValues: submittedOrder.orderItems.map(
          oi => `" ${CurrencyService.toUSDString(oi?.eachPrice.toFixed(2))}"`,
        ),
        totals: '',
      },
      {
        columnName: 'CS',
        columnValues: submittedOrder.orderItems.map(
          oi => `${oi.unitsOrdered.currentValue || 0}`,
        ),
        totals: `" ${submittedOrder.orderHeader.totalUnits}"`,
      },
      {
        columnName: 'EA',
        columnValues: submittedOrder.orderItems.map(
          oi => `${oi.eachesOrdered.currentValue || 0}`,
        ),
        totals: `" ${submittedOrder.orderHeader.totalEaches}"`,
      },
      {
        columnName: 'EXTENDED PRICE',
        columnValues: submittedOrder.orderItems.map(
          oi => `" ${CurrencyService.toUSDString(oi.totalDollars || 0)}"`,
        ),
        totals: `" ${CurrencyService.toUSDString(
          submittedOrder.orderHeader.totalDollars,
        )}"`,
      },
      {
        columnName: 'ORDER #',
        singleValue: `" ${submittedOrder.orderHeader.tandemOrderNumber}"`,
        totals: '',
      },
      { columnName: 'STOCK STATUS', singleValue: '', totals: '' },
      {
        columnName: 'EXCEPTIONS / AUTO-SUB',
        columnValues: OrderDownloadDtoService.getExceptionsForProducts(
          submittedOrder.orderHeader.orderExceptions,
          submittedOrder.orderItems.map(oi => oi.productNumber),
        ),
        totals: '',
      },
      { columnName: 'SHORTED', singleValue: '', totals: '' },
    ];
    return seperatedColumns;
  }

  /**
   *
   * @param products Contains product info
   * @param submittedOrders Contains Orders array (Header, OrderItems)
   * @returns
   */
  static createMultipleSeperatedColumnsForSubmitted(
    products: Map<number, Product>,
    submittedOrders: Order[],
  ): SeperatedColumn[] {
    const flatMap = arrays => {
      return !!arrays ? [].concat.apply([], arrays) : [];
    };
    let customerNumbers = [];
    let divisionNumbers = [];
    let departmentNumbers = [];
    let confirmedDeliveryDates = [];
    let poNumbers = [];
    let productNumbers = [];
    let productDescriptions = [];
    let productBrands = [];
    let productSizes = [];
    let csPrices = [];
    let eaPrices = [];
    let csUnitsTotals = [];
    let csTotalSum = 0;
    let eaUnitsTotals = [];
    let eaTotalSum = 0;
    let totalDollars = [];
    let totalDollarsSum = 0;
    let tandemOrderNumbers = [];
    let stockStatus = [];
    let orderExceptions = [];
    let orderHeaderExceptions = [];
    let orderItemsSub = [];

    submittedOrders.map(order => {
      orderItemsSub = order.orderItems
        ?.filter(oi => !!oi.substituteFor && oi.substituteFor > 0)
        ?.map(oi => oi.substituteFor);
      totalDollarsSum += order.orderHeader?.totalDollars || 0;
      orderHeaderExceptions = !!order.orderHeader?.orderExceptions
        ? [...order.orderHeader?.orderExceptions]
        : [];
      order.orderItems?.map(oi => {
        customerNumbers.push(order.orderHeader?.customerNumber || '');
        divisionNumbers.push(order.orderHeader?.divisionNumber || 0);
        departmentNumbers.push(order.orderHeader?.departmentNumber || 0);
        confirmedDeliveryDates.push(
          DateService.formatDateMaintainUTC(
            order.orderHeader?.confirmedDeliveryDate,
          ),
        );
        poNumbers.push(order.orderHeader?.purchaseOrderNumber || '');
        productNumbers.push(`${oi.productNumber}`);
        productDescriptions.push(
          `" ${OrderDownloadDtoService.escapeSeperatedValues(
            products.get(oi.productNumber)?.summary?.productDescTxtl ||
              'PRODUCT NOT FOUND',
          )}"`,
        );
        productBrands.push(
          `" ${OrderDownloadDtoService.escapeSeperatedValues(
            products.get(oi.productNumber)?.summary?.brand ||
              'PRODUCT NOT FOUND',
          )}"`,
        );
        productSizes.push(
          `" ${OrderDownloadDtoService.escapeSeperatedValues(
            products.get(oi.productNumber)?.summary?.salesPackSize ||
              'PRODUCT NOT FOUND',
          )}"`,
        );
        csPrices.push(
          `" ${CurrencyService.toUSDString(oi.unitPrice?.toFixed(2))}"`,
        );
        eaPrices.push(
          `" ${CurrencyService.toUSDString(oi.eachPrice?.toFixed(2))}"`,
        );
        csUnitsTotals.push(`${oi.unitsOrdered?.currentValue || 0}`);
        csTotalSum += oi.unitsOrdered?.currentValue || 0;
        eaUnitsTotals.push(`${oi.eachesOrdered?.currentValue || 0}`);
        eaTotalSum += oi.eachesOrdered?.currentValue || 0;
        totalDollars.push(
          `" ${CurrencyService.toUSDString(oi.totalDollars || 0)}"`,
        );
        tandemOrderNumbers.push(order.orderHeader?.tandemOrderNumber || 0);
        if (orderItemsSub.includes(oi.productNumber)) {
          stockStatus.push('"Out of Stock - Will Ship If Available"');
        } else {
          stockStatus.push('');
        }
        if (oi.substituteFlag === 'A') {
          orderHeaderExceptions.push({
            errorMessage: `Auto-sub for ${oi.substituteFor}`,
            errorCode: -1,
            errorType: '',
            actionMessage: '',
            actionCode: '',
            productNumber: oi.productNumber,
            inStockDate: '',
            subOrReplaceItems: [],
            exceptionDetails: [],
          });
        }
      });

      orderExceptions.push(
        OrderDownloadDtoService.getExceptionsForProducts(
          orderHeaderExceptions,
          order.orderItems?.map(oi => oi.productNumber),
        ),
      );
    });

    const seperatedColumns: SeperatedColumn[] = [
      {
        columnName: 'CUSTOMER NUMBER',
        columnValues: flatMap(customerNumbers),
        totals: '',
      },
      {
        columnName: 'DISTRIBUTOR',
        columnValues: flatMap(divisionNumbers),
        totals: '',
      },
      {
        columnName: 'DEPARTMENT',
        columnValues: flatMap(departmentNumbers),
        totals: '',
      },
      {
        columnName: 'DATE',
        columnValues: flatMap(confirmedDeliveryDates),
        totals: '',
      },
      {
        columnName: 'PO NUMBER',
        columnValues: flatMap(poNumbers),
        totals: 'Totals',
      },
      {
        columnName: 'PRODUCT NUMBER',
        columnValues: flatMap(productNumbers),
        totals: '',
      },
      {
        columnName: 'CUST PROD #',
        singleValue: '',
        totals: '',
      },
      {
        columnName: 'DESCRIPTION',
        columnValues: flatMap(productDescriptions),
        totals: '',
      },
      {
        columnName: 'BRAND',
        columnValues: flatMap(productBrands),
        totals: '',
      },
      {
        columnName: 'PACK SIZE',
        columnValues: flatMap(productSizes),
        totals: '',
      },
      {
        columnName: 'CS PRICE',
        columnValues: flatMap(csPrices),
        totals: '',
      },
      {
        columnName: 'EA PRICE',
        columnValues: flatMap(eaPrices),
        totals: '',
      },
      {
        columnName: 'CS',
        columnValues: flatMap(csUnitsTotals),
        totals: `" ${csTotalSum}"`,
      },
      {
        columnName: 'EA',
        columnValues: flatMap(eaUnitsTotals),
        totals: `" ${eaTotalSum}"`,
      },
      {
        columnName: 'EXTENDED PRICE',
        columnValues: flatMap(totalDollars),
        totals: `" ${CurrencyService.toUSDString(totalDollarsSum)}"`,
      },
      {
        columnName: 'ORDER #',
        columnValues: flatMap(tandemOrderNumbers),
        totals: '',
      },
      {
        columnName: 'STOCK STATUS',
        columnValues: flatMap(stockStatus),
        totals: '',
      },
      {
        columnName: 'EXCEPTIONS / AUTO-SUB',
        columnValues: flatMap(orderExceptions),
        totals: '',
      },
      { columnName: 'SHORTED', singleValue: '', totals: '' },
    ];
    return seperatedColumns;
  }

  static createSeperatedColumnsForMyOrders(
    orders: Order[],
    customers: Customer[],
  ): SeperatedColumn[] {
    const flatMap = arrays => {
      return !!arrays ? [].concat.apply([], arrays) : [];
    };

    const customerDictionary = {};

    customers?.map(c => {
      customerDictionary[c.customerNumber] = c;
    });

    let customerNames = [];
    let customerNumbers = [];
    let divisionNumbers = [];
    let departmentNumbers = [];
    let orderStatuses = [];
    let tandemOrderNumbers = [];
    let poNumbers = [];
    let csOrderedTotals = [];
    let eaOrderedTotals = [];
    let csReservedTotals = [];
    let eaReservedTotals = [];
    let totalDollars = [];
    let orderPlacedDates = [];
    let orderTimes = [];
    let estimatedDeliveryDates = [];
    let lastUpdatedBy = [];

    orders.forEach(order => {
      const orderHeader = order.orderHeader;
      const orderItems = order.orderItems || [];

      const { unitsOrdered, eachesOrdered, unitsReserved, eachesReserved } =
        orderItems?.reduce(
          (prevSum, item) => ({
            unitsOrdered:
              prevSum.unitsOrdered + (item.unitsOrdered?.currentValue || 0),
            eachesOrdered:
              prevSum.eachesOrdered + (item.eachesOrdered?.currentValue || 0),
            unitsReserved: prevSum.unitsReserved + (item?.unitsReserved || 0),
            eachesReserved:
              prevSum.eachesReserved + (item?.eachesReserved || 0),
          }),
          {
            unitsOrdered: 0,
            eachesOrdered: 0,
            unitsReserved: 0,
            eachesReserved: 0,
          },
        );
      const orderTotal =
        (orderHeader?.totalDollars || 0) +
          (orderHeader?.totalSalesTax || 0) +
          (orderHeader?.totalCharges || 0) -
          Math.abs(orderHeader?.totalAllowances || 0) || 0;

      const orderPlacedDate = orderHeader?.entryDtm || orderHeader?.orderDate;

      const lastUpdatedByUser =
        orderHeader?.updateUserId || orderHeader?.addUserId;

      const customerName =
        customerDictionary[orderHeader?.customerNumber]?.customerName || '';
      customerNames.push(`" ${customerName}"`);
      customerNumbers.push(orderHeader?.customerNumber || '');
      divisionNumbers.push(orderHeader?.divisionNumber || '');
      departmentNumbers.push(orderHeader?.departmentNumber || '');
      orderStatuses.push(
        OrderStatusDetails[orderHeader?.orderStatus]?.title || '',
      );
      tandemOrderNumbers.push(orderHeader?.tandemOrderNumber || '');
      poNumbers.push(orderHeader?.purchaseOrderNumber || '');
      csOrderedTotals.push(unitsOrdered);
      eaOrderedTotals.push(eachesOrdered);
      csReservedTotals.push(unitsReserved);
      eaReservedTotals.push(eachesReserved);
      totalDollars.push(`" ${CurrencyService.toUSDString(orderTotal)}"`);
      orderPlacedDates.push(
        !!orderPlacedDate
          ? DateService.formatDateMaintainUTC(orderPlacedDate)
          : '',
      );
      orderTimes.push(
        !!orderPlacedDate ? new Date(orderPlacedDate).toLocaleTimeString() : '',
      );
      estimatedDeliveryDates.push(
        !!orderHeader?.confirmedDeliveryDate
          ? DateService.formatDateMaintainUTC(
              orderHeader?.confirmedDeliveryDate,
            )
          : '',
      );
      lastUpdatedBy.push(lastUpdatedByUser || '');
    });

    const seperatedColumns: SeperatedColumn[] = [
      {
        columnName: 'CUSTOMER',
        columnValues: flatMap(customerNames),
        totals: '',
      },
      {
        columnName: 'CUSTOMER NUMBER',
        columnValues: flatMap(customerNumbers),
        totals: '',
      },
      {
        columnName: 'DEPARTMENT',
        columnValues: flatMap(departmentNumbers),
        totals: '',
      },
      {
        columnName: 'DIVISION #',
        columnValues: flatMap(divisionNumbers),
        totals: '',
      },
      {
        columnName: 'ORDER STATUS',
        columnValues: flatMap(orderStatuses),
        totals: '',
      },
      {
        columnName: 'ORDER #',
        columnValues: flatMap(tandemOrderNumbers),
        totals: '',
      },
      {
        columnName: 'PO #',
        columnValues: flatMap(poNumbers),
        totals: '',
      },
      {
        columnName: 'CASES ORDERED',
        columnValues: flatMap(csOrderedTotals),
        totals: '',
      },
      {
        columnName: 'EACHES ORDERED',
        columnValues: flatMap(eaOrderedTotals),
        totals: '',
      },
      {
        columnName: 'CASES RESERVED',
        columnValues: flatMap(csReservedTotals),
        totals: '',
      },
      {
        columnName: 'EACHES RESERVED',
        columnValues: flatMap(eaReservedTotals),
        totals: '',
      },
      {
        columnName: 'TOTAL $',
        columnValues: flatMap(totalDollars),
        totals: '',
      },
      {
        columnName: 'ORDER PLACED',
        columnValues: flatMap(orderPlacedDates),
        totals: '',
      },
      {
        columnName: 'ORDER TIME',
        columnValues: flatMap(orderTimes),
        totals: '',
      },
      {
        columnName: 'EST DELIVERY DATE',
        columnValues: flatMap(estimatedDeliveryDates),
        totals: '',
      },
      {
        columnName: 'LAST UPDATED BY',
        columnValues: flatMap(lastUpdatedBy),
        totals: '',
      },
    ];

    return seperatedColumns;
  }

  static createSeperatedColumnsForCart(
    products: Map<number, Product>,
    cartOrder: Order,
  ) {
    const flatMap = arrays => {
      return !!arrays ? [].concat.apply([], arrays) : [];
    };
    let productNumbers = [];
    let productDescriptions = [];
    let productBrands = [];
    let productSizes = [];
    let prices = [];
    let uom = [];
    let csUnitsTotals = [];
    let csTotalSum = 0;
    let eaUnitsTotals = [];
    let eaTotalSum = 0;
    let stockStatus = [];
    let orderExceptions = [];
    let orderHeaderExceptions = [];

    orderHeaderExceptions = !!cartOrder.orderHeader?.orderExceptions
      ? [...cartOrder.orderHeader?.orderExceptions]
      : [];

    cartOrder.orderItems.forEach(item => {
      const product = products.get(item.productNumber);

      productNumbers.push(`${item.productNumber}`);
      productDescriptions.push(
        `" ${OrderDownloadDtoService.escapeSeperatedValues(
          products.get(item.productNumber)?.summary?.productDescTxtl ||
            'PRODUCT NOT FOUND',
        )}"`,
      );
      productBrands.push(
        `" ${OrderDownloadDtoService.escapeSeperatedValues(
          products.get(item.productNumber)?.summary?.brand ||
            'PRODUCT NOT FOUND',
        )}"`,
      );
      productSizes.push(
        `" ${OrderDownloadDtoService.escapeSeperatedValues(
          products.get(item.productNumber)?.summary?.salesPackSize ||
            'PRODUCT NOT FOUND',
        )}"`,
      );

      csTotalSum += item?.unitsOrdered?.currentValue || 0;
      eaTotalSum += item?.eachesOrdered?.currentValue || 0;

      let hasPrice = false;

      if (
        product?.pricing?.unitPrice !== undefined &&
        product?.pricing?.unitPrice * 1 > 0 &&
        item?.unitsOrdered?.currentValue > 0
      ) {
        hasPrice = true;
        prices.push(
          `" ${CurrencyService.toUSDString(
            Number(product?.pricing?.unitPrice).toFixed(2),
          )}"`,
        );
        uom.push(
          `" ${
            product?.summary?.catchWeightFlag
              ? product?.summary?.priceUom
              : product?.summary?.priceUom || 'CS'
          }"`,
        );
      } else if (
        !product?.summary?.catchWeightFlag &&
        item?.eachesOrdered?.currentValue > 0 &&
        product?.pricing?.eachPrice !== undefined &&
        product?.pricing?.eachPrice * 1 > 0 &&
        product?.pricing?.eachPrice !== product?.pricing?.unitPrice
      ) {
        hasPrice = true;
        prices.push(
          `" ${CurrencyService.toUSDString(
            Number(product?.pricing?.eachPrice).toFixed(2),
          )}"`,
        );
        uom.push(
          `" ${(product?.summary?.catchWeightFlag && product?.summary?.breakable ? product?.summary.priceUom : product?.summary?.eachUom) || 'EA'}"`,
        );
      } else {
        prices.push(` No Price`);
        uom.push('');
      }

      csUnitsTotals.push(`${item.unitsOrdered.currentValue || '0'}`);
      eaUnitsTotals.push(`${item.eachesOrdered.currentValue || '0'}`);

      if (product?.summary?.properties?.has(ProductPropertiesEnum.outOfStock)) {
        stockStatus.push('"Out of Stock - Will Ship If Available"');
      } else {
        stockStatus.push('');
      }

      if (item.substituteFlag === 'A') {
        orderHeaderExceptions.push({
          errorMessage: `Auto-sub for ${item.substituteFor}`,
          errorCode: -1,
          errorType: '',
          actionMessage: '',
          actionCode: '',
          productNumber: item.productNumber,
          inStockDate: '',
          subOrReplaceItems: [],
          exceptionDetails: [],
        });
      }
    });

    orderExceptions.push(
      OrderDownloadDtoService.getExceptionsForProducts(
        orderHeaderExceptions,
        cartOrder.orderItems?.map(oi => oi.productNumber),
      ),
    );

    const seperatedColumns: SeperatedColumn[] = [
      {
        columnName: 'PRODUCT NUMBER',
        columnValues: flatMap(productNumbers),
        totals: '',
      },
      {
        columnName: 'DESCRIPTION',
        columnValues: flatMap(productDescriptions),
        totals: '',
      },
      {
        columnName: 'BRAND',
        columnValues: flatMap(productBrands),
        totals: '',
      },
      {
        columnName: 'PACK SIZE',
        columnValues: flatMap(productSizes),
        totals: '',
      },
      {
        columnName: 'PRICE',
        columnValues: flatMap(prices),
        totals: '',
      },
      {
        columnName: 'PRODUCT UOM',
        columnValues: flatMap(uom),
        totals: '',
      },
      {
        columnName: 'CASE QUANTITY',
        columnValues: flatMap(csUnitsTotals),
        totals: '',
      },
      {
        columnName: 'EACH QUANTITY',
        columnValues: flatMap(eaUnitsTotals),
        totals: '',
      },
      {
        columnName: 'STOCK STATUS',
        columnValues: flatMap(stockStatus),
        totals: '',
      },
      {
        columnName: 'EXCEPTIONS / AUTO-SUB',
        columnValues: flatMap(orderExceptions),
        totals: '',
      },
      { columnName: 'SHORTED', singleValue: '', totals: '' },
    ];

    return seperatedColumns;
  }
  /**
   *
   * @param seperatedColumns Contains Header, Data, Totals
   * @param numberOfRows The Number of Data Rows
   * @param seperator Tab by Default
   * @returns
   */
  static stringifySeparatedValues(
    seperatedColumns: SeperatedColumn[],
    numberOfRows: number,
    seperator: string,
  ): string {
    // Variable to hold the seperated columns as one string
    let stringified = '';
    // Create the Header Row
    const headerRow = seperatedColumns
      .map(pMap => pMap.columnName)
      .join(seperator);
    // Create the Totals Row
    const totalsRow = seperatedColumns.map(pMap => pMap.totals).join(seperator);
    // Add the header row to return string
    stringified += headerRow + '\r\n';
    // Add every row of data to return string
    for (let i = 0; i < numberOfRows; i++) {
      const line = seperatedColumns
        .map(pm => (pm.columnValues ? pm.columnValues[i] : pm.singleValue))
        .join(seperator);
      stringified += line + '\r\n';
    }
    // Add the totals row to the return string
    stringified += totalsRow + '\r\n';
    return stringified;
  }

  static escapeSeperatedValues(value: string): string {
    return value?.replace(`"`, `""`);
  }

  static getExceptionsForProducts(
    orderExceptions: OrderException[],
    productNumbers: number[],
  ): string[] {
    const productExceptionMessages: string[] = [];
    for (const productNumber of productNumbers) {
      const exception = orderExceptions.find(
        exception => exception.productNumber === productNumber,
      );
      productExceptionMessages.push(
        exception ? `" ${exception.errorMessage}"` : '',
      );
    }
    return productExceptionMessages;
  }

  private static stdCurrency(value: number) {
    return formatCurrency(value, 'en-US', '', 'USD', '1.2-2');
  }

  static formatMultiOrdersToCMP(
    orders: Order[],
    products: Map<number, Product>,
  ): string {
    let cmp = [];
    for (let order of orders) {
      cmp.push(
        this.formatOrderToCMP(order.orderHeader, order.orderItems, products),
      );
    }
    return cmp.join('');
  }

  static formatOrderToCMP(
    orderHeader: OrderHeader,
    orderItems: OrderItem[],
    products: Map<number, Product>,
  ): string {
    //According to STD 855 file format, refer to IEX_StandardFileFormats.pdf pg. 17 for formatting guide
    let formatCMP!: string;

    formatCMP = `ISA~  ~${' '.repeat(10)}~  ~${' '.repeat(10)}~  ~${' '.repeat(
      15,
    )}~  ~${' '.repeat(15)}~000000~0000~ ~${' '.repeat(5)}~900000001~ ~ ~~\r`;

    formatCMP += `ST~855~${'0'.repeat(9)}\r`;

    formatCMP += `BAK~06~AC~${orderHeader.purchaseOrderNumber.padEnd(
      20,
    )}${'~'.repeat(6)}${orderHeader.tandemOrderNumber.toString().padEnd(6)}~\r`;

    formatCMP += `N1~BY~~91~${(
      orderHeader.customerNumber.toString() +
      orderHeader.departmentNumber.toString()
    ).padEnd(10, '0')}\r`;

    formatCMP += `DTM~017~${DateService.formatShortDateMaintainUTC(
      orderHeader.confirmedDeliveryDate,
      '',
    )}~\r`;

    let fullQtyReserved = 0;
    let partQtyReserved = 0;
    const totalItems = orderItems.length;

    for (const item of orderItems) {
      formatCMP += `PO1~~`;

      let isCaseQtyPresent = false;
      if (item.unitsOrdered.currentValue > 0) {
        const unitsOrdered = item.unitsOrdered.currentValue
          .toString()
          .padStart(4, '0');
        //Format requires unitPrice to not include a '.', so this removes it using split and join

        const unitPrice = this.stdCurrency(item.unitPrice)
          .split('.')
          .join('')
          .split(',')
          .join('')
          .padStart(5, '0');
        formatCMP += `${unitsOrdered}~CA~${unitPrice}`;
        isCaseQtyPresent = true;
      } else {
        const eachesOrdered = item.eachesOrdered.currentValue
          .toString()
          .padStart(4, '0');
        const eachPrice = this.stdCurrency(item.eachPrice)
          .split('.')
          .join('')
          .padStart(5, '0');
        formatCMP += `${eachesOrdered}~EA~${eachPrice}`;
      }

      const product = products?.get(item.productNumber);

      formatCMP += `~~VN~${item.productNumber.toString().padStart(7, '0')}~IN~${
        product?.customerProductNumber
          ? product.customerProductNumber.padEnd(10, '0').slice(0, 10)
          : ' '.repeat(10)
      }`;

      formatCMP += `~PD~${product?.summary?.productDescTxtl
        ?.padEnd(30, ' ')
        .slice(0, 30)}`;

      formatCMP += `~BL~${product?.summary?.brand
        ?.padEnd(10, ' ')
        .slice(0, 10)}`;

      formatCMP += `~ZZ~${'0'.repeat(5)}${this.stdCurrency(item.totalDollars)
        .split('.')
        .join('')
        .split(',')
        .join('')
        .padStart(8, '0')}`;

      formatCMP += `${'~'.repeat(10)}\r`;

      if (isCaseQtyPresent) {
        if (item.eachesOrdered.currentValue > 0) {
          formatCMP += `SLN~1~~A~${item.eachesOrdered.currentValue
            .toString()
            .padStart(3, '0')}~EA`;
          formatCMP += `${'~'.repeat(23)}\r`;
        }
      }

      formatCMP += `PO4~~~~${product?.summary?.salesPackSize?.padEnd(
        12,
        ' ',
      )}~\r`;

      if (item.unitsOrdered.currentValue > 0) {
        formatCMP += `ACK~AR~${item.unitsReserved
          .toString()
          .padStart(4, '0')}~CA${'~'.repeat(23)}\r`;
        fullQtyReserved += item.unitsReserved;
      } else {
        formatCMP += `ACK~AR~${item.eachesReserved
          .toString()
          .padStart(4, '0')}~EA${'~'.repeat(23)}\r`;
        partQtyReserved += item.eachesReserved;
      }
    }

    const orderTotal = this.stdCurrency(
      (orderHeader.totalDollars || 0) +
        (orderHeader.totalSalesTax || 0) +
        (orderHeader.totalCharges || 0) -
        Math.abs(orderHeader.totalAllowances || 0) || 0,
    );

    formatCMP += `CTT~${totalItems.toString().padStart(4, '0')}${'~'.repeat(
      6,
    )}${fullQtyReserved.toString().padStart(7, '0')}${partQtyReserved
      .toString()
      .padStart(5, '0')}${'0'.repeat(21)}${orderTotal
      .split('.')
      .join('')
      .split(',')
      .join('')
      .padStart(9, '0')}\r`;

    //Counts the number of segments (lines) in the current file using regex
    const segmentCount = formatCMP.split(/\r\n|\r|\n/).length - 1;

    formatCMP += `SE~${segmentCount.toString().padStart(6, '0')}~${'0'.repeat(
      9,
    )}\r`;

    formatCMP += `IEA~${'1'.padStart(5, '0')}~900000001\r`;

    return formatCMP;
  }

  static formatMultiOrdersToSTD2(
    orders: Order[],
    products: Map<number, Product>,
  ): string {
    let std2 = [];
    for (let order of orders) {
      std2.push(
        this.formatOrderToSTD2(order.orderHeader, order.orderItems, products),
      );
    }
    return std2.join('');
  }

  static formatOrderToSTD2(
    orderHeader: OrderHeader,
    orderItems: OrderItem[],
    products: Map<number, Product>,
  ): string {
    try {
      let formatSTD2 = '';

      for (const item of orderItems) {
        formatSTD2 += (
          orderHeader?.customerNumber.toString() +
          orderHeader?.departmentNumber.toString()
        ).padEnd(10, '0');

        formatSTD2 += orderHeader.tandemOrderNumber.toString().padEnd(6);

        formatSTD2 += DateService.formatShortDateMaintainUTC(
          orderHeader.confirmedDeliveryDate,
          '',
        );

        const product = products.get(item.productNumber);

        formatSTD2 += orderHeader.purchaseOrderNumber.padEnd(20, ' ');

        formatSTD2 += '0'.repeat(9);

        formatSTD2 += item.productNumber.toString().padStart(7, '0');

        formatSTD2 += product?.customerProductNumber
          ? product.customerProductNumber.padEnd(10, '0').slice(0, 10)
          : ' '.repeat(10);

        formatSTD2 +=
          item.unitsReserved.toString().padStart(4, '0') +
          item.eachesReserved.toString().padStart(3, '0');

        const unitsOrdered = item.unitsOrdered.currentValue || 0;
        const eachesOrdered = item.eachesOrdered.currentValue || 0;

        formatSTD2 += `${unitsOrdered.toString().padStart(4, '0') || '0000'}${
          eachesOrdered.toString().padStart(3, '0') || '000'
        }`;

        if (item.eachesReserved > 0) {
          formatSTD2 += this.stdCurrency(item.eachPrice)
            .split('.')
            .join('')
            .padStart(5, '0');
        } else {
          formatSTD2 += this.stdCurrency(item.unitPrice)
            .split('.')
            .join('')
            .padStart(5, '0');
        }

        formatSTD2 += item.priceUOM.padStart(2, ' ');
        formatSTD2 += '0'.repeat(5); //Allowance
        formatSTD2 += '0'.repeat(5); //Break Charge?
        formatSTD2 += '0'.repeat(5); //Sales Tax?

        formatSTD2 += this.stdCurrency(item.totalDollars)
          .split('.')
          .join('')
          .split(',')
          .join('')
          .padStart(8, '0');

        formatSTD2 +=
          product.summary?.productDescTxtl?.padEnd(30, ' ').slice(0, 30) +
          product.summary?.salesPackSize?.padEnd(12, ' ') +
          product.summary?.brand?.padEnd(10, ' ').slice(0, 10);

        formatSTD2 += '\r\n';
      }

      return formatSTD2;
    } catch (error) {
      throw error;
    }
  }

  static formatOrdersToXML(
    orders: Order[],
    products: Map<number, Product>,
  ): string {
    const xml = document.implementation.createDocument(
      '',
      '',
      null,
    ) as XMLDocument;
    const root = xml.createElement('USFOrderResponse');
    const pi = xml.createProcessingInstruction(
      'xml',
      'version="1.0" encoding="UTF-8"',
    );
    xml.insertBefore(pi, xml.firstChild);

    for (const order of orders) {
      let orderItemsSub = order.orderItems
        ?.filter(oi => !!oi.substituteFor && oi.substituteFor > 0)
        ?.map(oi => oi.substituteFor);

      const orderSummary = xml.createElement('OrderSummary');

      const orderHeaderEle = xml.createElement('OrderHeader');

      orderHeaderEle.appendChild(
        this.createXMLElement(
          xml,
          'DistributorNumber',
          order.orderHeader.divisionNumber.toString(),
        ),
      );
      orderHeaderEle.appendChild(
        this.createXMLElement(
          xml,
          'CustomerNumber',
          order.orderHeader.customerNumber.toString(),
        ),
      );
      orderHeaderEle.appendChild(
        this.createXMLElement(
          xml,
          'DepartmentNumber',
          order.orderHeader.departmentNumber.toString(),
        ),
      );
      orderHeaderEle.appendChild(
        this.createXMLElement(
          xml,
          'CustomerPONumber',
          order.orderHeader.purchaseOrderNumber,
        ),
      );
      orderHeaderEle.appendChild(
        this.createDateXMLElement(
          xml,
          'RequestedDeliveryDate',
          new Date(order.orderHeader.requestedDeliveryDate),
        ),
      );
      orderHeaderEle.appendChild(
        this.createDateXMLElement(
          xml,
          'ConfirmedDeliveryDate',
          new Date(order.orderHeader.confirmedDeliveryDate),
        ),
      );
      orderHeaderEle.appendChild(
        this.createXMLElement(
          xml,
          'OrderNumber',
          order.orderHeader.tandemOrderNumber.toString(),
        ),
      );
      orderHeaderEle.appendChild(
        this.createCurrencyXMLElement(
          xml,
          'OrderTotal',
          order.orderHeader.totalDollars,
        ),
      );
      orderSummary.appendChild(orderHeaderEle);

      for (const item of order.orderItems) {
        const product = products.get(item.productNumber);
        const orderItemEle = xml.createElement('OrderItem');

        const productNumber = product?.productNumber;

        orderItemEle.appendChild(
          this.createXMLElement(
            xml,
            'DistributorProductNumber',
            productNumber
              ? productNumber.toString()
              : item.productNumber?.toString(),
          ),
        );
        orderItemEle.appendChild(
          this.createXMLElement(
            xml,
            'DistributorLineSequenceNumber',
            item.sequence.toString(),
          ),
        );
        orderItemEle.appendChild(
          this.createXMLElement(
            xml,
            'CaseQuantityOrdered',
            item.unitsOrdered.currentValue
              ? item.unitsOrdered.currentValue.toString()
              : '0',
          ),
        );
        orderItemEle.appendChild(
          this.createXMLElement(
            xml,
            'CaseQuantityReserved',
            item.unitsReserved.toString() || '0',
          ),
        );
        orderItemEle.appendChild(
          this.createCurrencyXMLElement(
            xml,
            'CasePriceConfirmed',
            item.unitPrice,
          ),
        );
        orderItemEle.appendChild(
          this.createXMLElement(
            xml,
            'EachQuantityOrdered',
            item.eachesOrdered.currentValue
              ? item.eachesOrdered.currentValue.toString()
              : '0',
          ),
        );
        orderItemEle.appendChild(
          this.createXMLElement(
            xml,
            'EachQuantityReserved',
            item.eachesReserved.toString() || '0',
          ),
        );
        orderItemEle.appendChild(
          this.createCurrencyXMLElement(
            xml,
            'EachPriceConfirmed',
            item.eachPrice,
          ),
        );
        orderItemEle.appendChild(
          this.createCurrencyXMLElement(
            xml,
            'ExtendedPrice',
            item.totalDollars,
          ),
        );
        orderItemEle.appendChild(
          this.createXMLElement(
            xml,
            'StockStatus',
            orderItemsSub.includes(item.productNumber)
              ? 'Out of Stock - Will Ship If Available'
              : '',
          ),
        );
        orderItemEle.appendChild(
          this.createXMLElement(
            xml,
            'ExceptionsAutoSub',
            item.substituteFlag === 'A'
              ? `'Auto-sub for ${item.substituteFor}'`
              : '',
          ),
        );

        orderSummary.appendChild(orderItemEle);
      }
      root.appendChild(orderSummary);
    }

    xml.appendChild(root);
    return new XMLSerializer().serializeToString(xml);
  }

  private static createXMLElement(
    doc: XMLDocument,
    elementName: string,
    value: string,
  ) {
    const element = doc.createElement(elementName);
    element.innerHTML = value;
    doc.appendChild(element);
    return element;
  }

  private static createDateXMLElement(
    doc: XMLDocument,
    elementName: string,
    date: Date,
  ) {
    const element = doc.createElement(elementName);
    element.appendChild(
      this.createXMLElement(doc, 'Year', date.getUTCFullYear().toString()),
    );
    element.appendChild(
      this.createXMLElement(doc, 'Month', (date.getUTCMonth() + 1).toString()),
    );
    element.appendChild(
      this.createXMLElement(doc, 'Day', date.getUTCDate().toString()),
    );
    return element;
  }

  private static createCurrencyXMLElement(
    doc: XMLDocument,
    elementName: string,
    amount: number,
  ) {
    const element = doc.createElement(elementName);
    const currencyAmount = doc.createElement('CurrencyAmount');
    currencyAmount.appendChild(this.createXMLElement(doc, 'Currency', 'USD'));
    currencyAmount.appendChild(
      this.createXMLElement(doc, 'Amount', this.stdCurrency(amount)),
    );
    element.appendChild(currencyAmount);
    return element;
  }
}
